import { lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import Layout from './components/common/Layout';
import ErrorBoundary from './components/common/ErrorBoundary';
import AuthGuard from './components/auth/AuthGuard';
import MissionTrackerHost from './components/missions/MissionTrackerHost';
import Unauthorized from './components/common/Unauthorized';
import { useRole } from './hooks/useRole';

// Route-level code splitting — each page is a separate chunk
const ReplHome = lazy(() => import('./pages/ReplHome'));
const AgentLibrary = lazy(() => import('./pages/AgentLibrary'));
const AgentChat = lazy(() => import('./pages/AgentChat'));
const AgentDetail = lazy(() => import('./pages/AgentDetail'));
const ArtifactLibrary = lazy(() => import('./pages/ArtifactLibrary'));
const ProjectsPage = lazy(() => import('./pages/ProjectsPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const ConnectorsPage = lazy(() => import('./pages/ConnectorsPage'));
const ToolsPage = lazy(() => import('./pages/ToolsPage'));
const ToolMarketplacePage = lazy(() => import('./pages/ToolMarketplacePage'));
const OrchestrationDashboard = lazy(() => import('./pages/OrchestrationDashboard'));
const MissionDashboard = lazy(() => import('./pages/MissionDashboard'));
const MissionDetail = lazy(() => import('./pages/MissionDetail'));
const ApprovalQueue = lazy(() => import('./pages/ApprovalQueue'));
const ExternalAgentsPage = lazy(() => import('./pages/ExternalAgentsPage'));
const KnowledgeBasePage = lazy(() => import('./pages/KnowledgeBasePage'));
const KnowledgeHubPage = lazy(() => import('./pages/KnowledgeHubPage'));
const ArticleViewPage = lazy(() => import('./pages/ArticleViewPage'));
const ArticleEditorPage = lazy(() => import('./pages/ArticleEditorPage'));
const WorkspacePage = lazy(() => import('./pages/WorkspacePage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const McpTenantsPage = lazy(() => import('./pages/McpTenantsPage'));
const ReviewQueuePage = lazy(() => import('./pages/ReviewQueuePage'));
const SkillsHubPage = lazy(() => import('./pages/SkillsHubPage'));
const SkillViewPage = lazy(() => import('./pages/SkillViewPage'));
const SkillEditorPage = lazy(() => import('./pages/SkillEditorPage'));
const ObservabilityPage = lazy(() => import('./pages/ObservabilityPage'));
const OperationsPage = lazy(() => import('./pages/OperationsPage'));
const ReportTrackPage = lazy(() => import('./pages/ReportTrackPage'));
const ReportsBoardPage = lazy(() => import('./pages/ReportsBoardPage'));
const NotificationSettingsPage = lazy(() => import('./pages/NotificationSettingsPage'));
const FinOpsPage = lazy(() => import('./pages/FinOpsPage'));

function PageSkeleton() {
  return (
    <div className="p-6 space-y-4 animate-pulse">
      <div className="h-8 bg-surfaceAlt rounded w-1/3" />
      <div className="h-4 bg-surfaceAlt rounded w-2/3" />
      <div className="h-4 bg-surfaceAlt rounded w-1/2" />
      <div className="mt-6 h-64 bg-surfaceAlt/50 rounded-lg" />
    </div>
  );
}

function RoleGuard({ minRole, children }: { minRole: 'poweruser' | 'creator'; children: React.ReactNode }) {
  const { hasRole } = useRole();
  if (!hasRole(minRole)) {
    return <Unauthorized requiredRole={minRole} />;
  }
  return <>{children}</>;
}

function App() {
  return (
    <ErrorBoundary>
    <AuthGuard>
      {/* One provider for the whole signed-in app: Layout's toasts, the Watch buttons on mission
          pages and the settings panel all share a single client and a single poll. It sits
          inside AuthGuard because `getToken` is only meaningful once MSAL has a session, and
          inside the router because `onOpenMission` navigates. */}
      <MissionTrackerHost>
      <Layout>
        <Suspense fallback={<PageSkeleton />}>
        <Routes>
          <Route path="/" element={<ReplHome />} />
          <Route path="/agents" element={<AgentLibrary />} />
          <Route path="/agents/:id" element={<AgentDetail />} />
          <Route path="/agents/:id/chat" element={<AgentChat />} />
          <Route path="/artifacts" element={<ArtifactLibrary />} />
          <Route path="/tools" element={<ToolsPage />} />
          <Route path="/marketplace" element={<ToolMarketplacePage />} />
          <Route path="/orchestrations" element={<OrchestrationDashboard />} />
          <Route path="/orchestrations/:id" element={<OrchestrationDashboard />} />
          <Route path="/missions" element={<RoleGuard minRole="poweruser"><MissionDashboard /></RoleGuard>} />
          <Route path="/missions/approvals" element={<RoleGuard minRole="poweruser"><ApprovalQueue /></RoleGuard>} />
          <Route path="/missions/:id" element={<RoleGuard minRole="poweruser"><MissionDetail /></RoleGuard>} />
          <Route path="/external-agents" element={<ExternalAgentsPage />} />
          <Route path="/knowledge" element={<KnowledgeHubPage />} />
          <Route path="/knowledge/review" element={<RoleGuard minRole="creator"><ReviewQueuePage /></RoleGuard>} />
          <Route path="/knowledge/new" element={<RoleGuard minRole="poweruser"><ArticleEditorPage /></RoleGuard>} />
          <Route path="/knowledge/:slug/edit" element={<RoleGuard minRole="poweruser"><ArticleEditorPage /></RoleGuard>} />
          <Route path="/knowledge/:slug" element={<ArticleViewPage />} />
          <Route path="/knowledge-base" element={<KnowledgeBasePage />} />
          <Route path="/reports" element={<RoleGuard minRole="poweruser"><ReportsBoardPage /></RoleGuard>} />
          <Route path="/track/:id" element={<ReportTrackPage />} />
          {/* Skills: any member may read and author. A member's save always lands in review,
              and the API refuses an edit to a skill someone else authored. */}
          <Route path="/skills" element={<SkillsHubPage />} />
          <Route path="/skills/new" element={<SkillEditorPage />} />
          <Route path="/skills/:name/edit" element={<SkillEditorPage />} />
          <Route path="/skills/:name" element={<SkillViewPage />} />
          <Route path="/workspace" element={<RoleGuard minRole="poweruser"><WorkspacePage /></RoleGuard>} />
          <Route path="/profile" element={<RoleGuard minRole="poweruser"><ProfilePage /></RoleGuard>} />
          {/* Deliberately not role-gated: these are the signed-in user's own delivery
              preferences, and a member who receives report notifications needs to be able to
              turn them off even though /missions is poweruser-only. */}
          <Route path="/settings/notifications" element={<NotificationSettingsPage />} />
          <Route path="/connectors" element={<RoleGuard minRole="poweruser"><ConnectorsPage /></RoleGuard>} />
          <Route path="/projects" element={<RoleGuard minRole="poweruser"><ProjectsPage /></RoleGuard>} />
          <Route path="/mcp-tenants" element={<RoleGuard minRole="creator"><McpTenantsPage /></RoleGuard>} />
          <Route path="/operations" element={<RoleGuard minRole="poweruser"><OperationsPage /></RoleGuard>} />
          {/* Same gate the API enforces: `GET /api/finops/*` requires PowerUser. Matching them
              means an unauthorized user sees the role page rather than an empty report, which is
              the one rendering this feature must never produce. */}
          <Route path="/finops" element={<RoleGuard minRole="poweruser"><FinOpsPage /></RoleGuard>} />
          <Route path="/observability" element={<RoleGuard minRole="creator"><ObservabilityPage /></RoleGuard>} />
          <Route path="/admin" element={<RoleGuard minRole="creator"><AdminPage /></RoleGuard>} />
        </Routes>
        </Suspense>
      </Layout>
      </MissionTrackerHost>
    </AuthGuard>
    </ErrorBoundary>
  );
}

export default App;
